import {
  DEFAULT_FAMILY_LAYOUT, DEFAULT_HOME_LAYOUT, HOME_TEMPLATES, HOME_WIDGETS, addWidget, applyTemplate, hiddenWidgets, removeWidget, swapWidgets, templateDef, templateGrid,
  type HomeGrid, type HomeTemplateId, type HomeWidgetId,
} from '@obv/core';
import { FAMILY_BACKGROUNDS, useIdle } from '@obv/ui';
import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode, type TouchEvent as ReactTouchEvent } from 'react';
import { useUpdatePreferences } from '@/queries/hooks';
import { useDisplay } from '../context';
import { useDisplayUi, type HomeDraft } from '../store';
import { Button, Eyebrow } from '../ui';
import { ChorePills, FamilyTopBar, TimeBlock } from './DashboardParts';

const LONG_PRESS_MS = 650;
const EDIT_IDLE_MS = 3 * 60_000;
// Phone: one widget per row at a fixed height, the column scrolling as a whole.
const PHONE_HEIGHT: Partial<Record<HomeWidgetId, number>> = {
  clock: 230,
  now: 300,
  air: 330,
  agenda: 340,
  calendar: 400,
  week: 320,
  weather_hero: 300,
  forecast: 340,
  chores: 320,
  habits: 300,
  salat_split: 280,
  salat_list: 300,
  kanji: 300,
};
const PHONE_DEFAULT_HEIGHT = 260;
const PHONE_EMPTY_HEIGHT = 120;

const label = (id: HomeWidgetId) => (HOME_WIDGETS.find((w) => w.id === id) || { label: id }).label;

interface Drag {
  from: number;
  startX: number;
  startY: number;
  dx: number;
  dy: number;
  over: number | null;
}

type Point = { x: number; y: number };

function pointOf(e: TouchEvent | MouseEvent): Point | null {
  if ('touches' in e) {
    const t = e.touches[0] || e.changedTouches[0];
    return t ? { x: t.clientX, y: t.clientY } : null;
  }
  return { x: e.clientX, y: e.clientY };
}

function slotAt(p: Point): number | null {
  const el = document.elementFromPoint(p.x, p.y);
  const slot = el && (el as HTMLElement).closest ? ((el as HTMLElement).closest('[data-slot]') as HTMLElement | null) : null;
  return slot ? Number(slot.getAttribute('data-slot')) : null;
}

/**
 * The Today screen as movable widgets in a chosen layout template. Customize (button, long-press a
 * widget, or Settings) shows a layout picker, each slot with a remove button, and a tray of widgets
 * to add; drag one widget onto another to swap them. Safari 12 has no Pointer Events and no touch
 * drag-and-drop, so dragging is built on touch and mouse events, with a non-passive touchmove.
 */
export function CustomizableHome({
  eyebrow,
  pill,
  renderWidget,
  target = 'home',
}: {
  eyebrow: ReactNode;
  pill: ReactNode;
  renderWidget: (id: HomeWidgetId, narrow: boolean) => ReactNode;
  /**
   * `dashboard`: the Family dashboard scene; `family`: the "Our family" scene. Both use the fixed
   * dashboard template with their own widget list; `family` also picks its background here.
   */
  target?: 'home' | 'dashboard' | 'family';
}) {
  const ctx = useDisplay();
  const { homeDraft, startHomeEdit, setHomeDraft, endHomeEdit } = useDisplayUi();
  const prefs = useUpdatePreferences();
  const isFamily = target === 'family';
  const isDashboard = target === 'dashboard' || isFamily;
  const defaultDraft: HomeDraft = isFamily
    ? { template: 'dashboard', layout: DEFAULT_FAMILY_LAYOUT.slice() }
    : isDashboard
      ? { template: 'dashboard', layout: templateDef('dashboard').suggested.slice() }
      : { template: 'classic', layout: DEFAULT_HOME_LAYOUT.slice() };
  const saved: HomeDraft = isFamily
    ? { template: 'dashboard', layout: (ctx.prefs.family_layout as HomeWidgetId[]) || defaultDraft.layout }
    : isDashboard
    ? { template: 'dashboard', layout: (ctx.prefs.dashboard_layout as HomeWidgetId[]) || defaultDraft.layout }
    : {
        template: (ctx.prefs.home_template as HomeTemplateId) || 'classic',
        layout: (ctx.prefs.home_layout as HomeWidgetId[]) || DEFAULT_HOME_LAYOUT,
      };
  const editing = homeDraft !== null;
  const current = editing ? homeDraft! : saved;
  const layout = current.layout;
  const def = templateDef(current.template);
  // Fixed templates keep every slot in place; unfilled ones stay empty (shown only while editing).
  const slotCount = def.id === 'classic' ? layout.length : def.capacity;
  const phone = ctx.phone;
  const grid = phone ? phoneGrid(layout, slotCount, editing) : templateGrid(def.id, layout.length, ctx.orientation === 'portrait');
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  dragRef.current = drag;
  const isDragging = drag !== null;

  useIdle(EDIT_IDLE_MS, endHomeEdit, editing);
  // Leaving the screen discards an unsaved draft.
  useEffect(() => () => endHomeEdit(), [endHomeEdit]);

  const setLayout = (next: HomeWidgetId[]) => homeDraft && setHomeDraft({ ...homeDraft, layout: next });

  // Window-level listeners while dragging so the finger can leave the slot it started in.
  useEffect(() => {
    if (!isDragging) return;
    const move = (e: TouchEvent | MouseEvent) => {
      const p = pointOf(e);
      const d = dragRef.current;
      if (!p || !d) return;
      if (e.cancelable) e.preventDefault();
      setDrag({ ...d, dx: p.x - d.startX, dy: p.y - d.startY, over: slotAt(p) });
    };
    const end = () => {
      const d = dragRef.current;
      if (d && d.over !== null && d.over !== d.from && homeDraft && d.over < homeDraft.layout.length) {
        setHomeDraft({ ...homeDraft, layout: swapWidgets(homeDraft.layout, d.from, d.over) });
      }
      setDrag(null);
    };
    window.addEventListener('touchmove', move, { passive: false } as AddEventListenerOptions);
    window.addEventListener('mousemove', move);
    window.addEventListener('touchend', end);
    window.addEventListener('touchcancel', end);
    window.addEventListener('mouseup', end);
    return () => {
      window.removeEventListener('touchmove', move);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('touchend', end);
      window.removeEventListener('touchcancel', end);
      window.removeEventListener('mouseup', end);
    };
  }, [isDragging, homeDraft, setHomeDraft]);

  const beginDrag = (index: number, e: ReactTouchEvent | ReactMouseEvent) => {
    const p = 'touches' in e ? { x: e.touches[0]!.clientX, y: e.touches[0]!.clientY } : { x: e.clientX, y: e.clientY };
    setDrag({ from: index, startX: p.x, startY: p.y, dx: 0, dy: 0, over: index });
  };

  const save = () => {
    if (homeDraft) {
      prefs.mutate(
        isFamily ? { family_layout: homeDraft.layout } : isDashboard ? { dashboard_layout: homeDraft.layout } : { home_template: homeDraft.template, home_layout: homeDraft.layout },
      );
    }
    endHomeEdit();
  };
  const hidden = editing ? hiddenWidgets(layout) : [];
  const full = layout.length >= def.capacity;
  const showHeader = editing || !ctx.chromeHidden;

  // A phone scrolls the top bar away with the widgets; there is no room to pin it.
  const body = (
    <>
      {def.topBar && isFamily ? <FamilyTopBar className={showHeader && !phone ? 'mt-3' : ''} /> : null}
      {def.topBar && !isFamily ? (
        <div className={`flex h-[76px] flex-none items-center justify-between ${showHeader && !phone ? 'mt-3' : ''}`}>
          <ChorePills />
          <TimeBlock />
        </div>
      ) : null}

      <div
        className={phone ? `grid ${def.topBar ? 'mt-3' : ''}` : `${showHeader || def.topBar ? 'mt-4' : ''} grid min-h-0 flex-1`}
        style={{ gridTemplateColumns: grid.columns, gridTemplateRows: grid.rows, gridTemplateAreas: grid.areas, gridGap: phone ? 12 : 14 }}
      >
        {Array.from({ length: slotCount }, (_, i) => {
          const id = layout[i];
          if (!id) {
            return editing ? <EmptySlot key={`empty-${i}`} index={i} /> : <div key={`empty-${i}`} style={{ gridArea: `s${i}` }} />;
          }
          return (
            <Slot
              key={id}
              index={i}
              id={id}
              editing={editing}
              drag={drag}
              canRemove={layout.length > 1}
              onLongPress={() => startHomeEdit(saved)}
              onDragStart={(e) => beginDrag(i, e)}
              onRemove={() => setLayout(removeWidget(layout, id))}
            >
              {renderWidget(id, grid.narrow[i] || false)}
            </Slot>
          );
        })}
      </div>
    </>
  );

  return (
    <>
      {showHeader ? (
        <header className={`flex flex-none justify-between ${phone && editing ? 'flex-col items-start' : 'items-center'}`}>
          <div className="min-w-0">
            <Eyebrow>{editing ? (isFamily ? 'Customize “Our family”' : isDashboard ? 'Customize dashboard' : 'Customize home') : eyebrow}</Eyebrow>
            {editing ? (
              <div className="mt-1 text-[13px] text-muted">
                {isDashboard ? 'Drag a widget onto another to swap them, remove or add widgets.' : 'Pick a layout, then drag a widget onto another to swap them.'}{' '}
                {def.label} holds {def.capacity}.
              </div>
            ) : null}
          </div>
          <div className={`flex flex-none items-center ${phone && editing ? 'mt-2' : 'ml-4'}`}>
            {editing ? (
              <>
                <Button variant="ghost" onClick={() => setHomeDraft({ template: defaultDraft.template, layout: defaultDraft.layout.slice() })}>
                  Reset
                </Button>
                <Button className="ml-2" onClick={endHomeEdit}>
                  Cancel
                </Button>
                <Button variant="primary" className="ml-2" onClick={save}>
                  Done
                </Button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="tap mr-3 h-[44px] text-[13px] font-medium"
                  onClick={() => startHomeEdit(saved)}
                  aria-label={isFamily ? 'Customize Our family' : isDashboard ? 'Customize dashboard' : 'Customize home screen'}
                >
                  Customize
                </button>
                {pill}
              </>
            )}
          </div>
        </header>
      ) : null}

      {editing ? (
        <>
          {isDashboard ? null : (
          <div className="mt-3 flex flex-none items-center" role="radiogroup" aria-label="Layout">
            <span className="mr-3 text-[12px] font-semibold uppercase tracking-[0.1em] text-muted">Layout</span>
            <div className="scroll-x flex min-w-0 flex-1">
              {HOME_TEMPLATES.map((t) => (
                <TemplateChip
                  key={t.id}
                  id={t.id}
                  selected={t.id === def.id}
                  onClick={() => homeDraft && setHomeDraft({ template: t.id, layout: applyTemplate(homeDraft.layout, t.id) })}
                />
              ))}
            </div>
          </div>
          )}
          {isFamily ? <BackgroundPicker /> : null}
          <div className="mt-2 flex flex-none flex-wrap items-center" aria-label="Widgets you can add">
            {hidden.length === 0 ? <span className="text-[13px] text-muted">Every widget is on the screen.</span> : null}
            {hidden.map((id) => (
              <button
                key={id}
                type="button"
                disabled={full}
                onClick={() => setLayout(addWidget(layout, id, def.capacity))}
                className="tap mb-1 mr-2 inline-flex h-[36px] items-center rounded-full border border-bd px-3 text-[13px] disabled:opacity-40"
              >
                + {label(id)}
              </button>
            ))}
            {full && hidden.length ? <span className="mb-1 text-[12px] text-muted">Remove one to add another.</span> : null}
          </div>
        </>
      ) : null}

      {phone ? <div className={`scroll-y min-h-0 flex-1 ${showHeader ? 'mt-3' : ''}`}>{body}</div> : body}
    </>
  );
}

/** Phone grid: every slot full width, one per row, at its widget's height; all use compact forms. */
function phoneGrid(layout: HomeWidgetId[], slotCount: number, editing: boolean): HomeGrid {
  const rows: string[] = [];
  const areas: string[] = [];
  const narrow: boolean[] = [];
  for (let i = 0; i < slotCount; i++) {
    const id = layout[i];
    // Unfilled slots of fixed templates only take room while editing.
    const h = id ? PHONE_HEIGHT[id] || PHONE_DEFAULT_HEIGHT : editing ? PHONE_EMPTY_HEIGHT : 0;
    rows.push(`${h}px`);
    areas.push(`"s${i}"`);
    narrow.push(true);
  }
  return { columns: 'minmax(0,1fr)', rows: rows.join(' '), areas: areas.join(' '), narrow };
}

/** Layout option with a miniature of its slots, drawn from the template's own grid. */
function TemplateChip({ id, selected, onClick }: { id: HomeTemplateId; selected: boolean; onClick: () => void }) {
  const def = templateDef(id);
  const g = templateGrid(id, def.capacity, false);
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={`${def.label} layout: ${def.desc}`}
      onClick={onClick}
      className="tap mr-2 flex h-[48px] flex-none items-center rounded-[12px] border px-2"
      style={{ borderColor: selected ? 'var(--ink)' : 'var(--bd)', background: selected ? 'var(--soft)' : 'transparent' }}
    >
      <span className="flex h-[32px] w-[44px] flex-col" aria-hidden>
        {def.topBar ? <span className="mb-[2px] h-[4px] rounded-[1px]" style={{ background: 'var(--ink)', opacity: 0.6 }} /> : null}
        <span
          className="grid min-h-0 flex-1"
          style={{ gridTemplateColumns: g.columns, gridTemplateRows: g.rows, gridTemplateAreas: g.areas, gridGap: 2 }}
        >
          {Array.from({ length: def.capacity }, (_, i) => (
            <span key={i} className="rounded-[2px]" style={{ gridArea: `s${i}`, background: 'var(--ink)', opacity: 0.35 }} />
          ))}
        </span>
      </span>
      <span className="ml-2 text-[13px] font-medium">{def.label}</span>
    </button>
  );
}

function EmptySlot({ index }: { index: number }) {
  return (
    <div
      data-slot={index}
      className="flex items-center justify-center rounded-[20px] text-[13px] text-muted"
      style={{ gridArea: `s${index}`, border: '1.5px dashed var(--bd)' }}
    >
      Empty — add a widget above
    </div>
  );
}

function Slot({
  index, id, editing, drag, canRemove, onLongPress, onDragStart, onRemove, children,
}: {
  index: number;
  id: HomeWidgetId;
  editing: boolean;
  drag: Drag | null;
  canRemove: boolean;
  onLongPress: () => void;
  onDragStart: (e: ReactTouchEvent | ReactMouseEvent) => void;
  onRemove: () => void;
  children: ReactNode;
}) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pressed = useRef(false);
  const dragging = drag !== null && drag.from === index;
  const target = drag !== null && drag.over === index && drag.from !== index;

  // Long-press anywhere on a widget enters customize mode; the click that ends the press is swallowed.
  const startPress = () => {
    if (editing) return;
    pressed.current = false;
    timer.current = setTimeout(() => {
      pressed.current = true;
      onLongPress();
    }, LONG_PRESS_MS);
  };
  const cancelPress = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  return (
    <div
      data-slot={index}
      className="relative grid min-h-0 min-w-0"
      style={{
        gridArea: `s${index}`,
        transform: dragging ? `translate(${drag!.dx}px, ${drag!.dy}px) scale(1.02)` : undefined,
        zIndex: dragging ? 20 : undefined,
        pointerEvents: dragging ? 'none' : undefined,
        transition: dragging ? 'none' : 'transform 180ms',
      }}
      onTouchStartCapture={startPress}
      onTouchEndCapture={cancelPress}
      onTouchMoveCapture={cancelPress}
      onMouseDownCapture={startPress}
      onMouseUpCapture={cancelPress}
      onMouseLeave={cancelPress}
      onClickCapture={(e) => {
        if (pressed.current) {
          e.stopPropagation();
          e.preventDefault();
          pressed.current = false;
        }
      }}
    >
      {children}
      {editing ? (
        <div
          className="cover flex flex-col items-center justify-center rounded-[20px]"
          style={{
            background: target ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.18)',
            border: `2px dashed ${target ? 'var(--ink)' : 'var(--bd)'}`,
            cursor: 'grab',
            touchAction: 'none',
          }}
          role="button"
          aria-label={`${label(id)}, slot ${index + 1}. Drag onto another widget to swap.`}
          onTouchStart={onDragStart}
          onMouseDown={onDragStart}
        >
          <span className="rounded-full px-4 py-2 text-[15px] font-medium" style={{ background: 'var(--sheet)', color: 'var(--ink)', boxShadow: '0 4px 14px rgba(0,0,0,0.18)' }}>
            ⠿ {label(id)}
          </span>
          {canRemove ? (
            <button
              type="button"
              aria-label={`Remove ${label(id)}`}
              className="tap absolute right-0 top-0 flex h-[44px] w-[44px] items-center justify-center"
              onTouchStart={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={onRemove}
            >
              <span className="flex h-[28px] w-[28px] items-center justify-center rounded-full text-[16px]" style={{ background: 'var(--ink)', color: 'var(--onInk)' }}>
                ×
              </span>
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** The "Our family" backgrounds; applied (and saved) as soon as one is tapped. */
function BackgroundPicker() {
  const ctx = useDisplay();
  const prefs = useUpdatePreferences();
  const current = ctx.prefs.family_background;
  return (
    <div className="mt-3 flex flex-none items-center" role="radiogroup" aria-label="Background">
      <span className="mr-3 text-[12px] font-semibold uppercase tracking-[0.1em] text-muted">Background</span>
      {FAMILY_BACKGROUNDS.map((b) => {
        const on = b.key === current;
        return (
          <button
            key={b.key}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => prefs.mutate({ family_background: b.key })}
            className="tap mr-2 inline-flex h-[44px] items-center rounded-full border pl-[6px] pr-4 text-[14px]"
            style={{ borderColor: on ? 'var(--ink)' : 'var(--bd)', boxShadow: on ? 'inset 0 0 0 1px var(--ink)' : 'none' }}
          >
            <span className="mr-2 h-[30px] w-[30px] rounded-full" style={{ background: b.swatch, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)' }} />
            {b.label}
          </button>
        );
      })}
    </div>
  );
}
